import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {JSONObject, JSONValue} from 'util/Types';

import {getConfigShapeAtConfigShapePath} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  BANNER_KEY,
  EXPANDABLE_DETAILS_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  INFO_TEXT_UI_SCHEMA_KEY,
  IS_COLLAPSIBLE_SCHEMA_KEY,
  isShapeUISchemaV2FieldComponentValue,
  isShapeUISchemaV2NestedShapeValues,
  NUM_COLUMNS_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaV2,
  ConfigShapePath,
  SHOW_DIVIDER_LINE_KEY,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  isFieldUISchemaWithStandardComponentV2,
  FieldUISchemaBanner,
} from '../StepConfigEditorTypes';
import {getSectionNameOrNull} from './SectionName';
import StepConfigEditorBanner from './StepConfigEditorBanner';
import StepConfigEditorRouterV2 from './StepConfigEditorRouterV2';
import StepConfigFieldsTemplate, {CollapsingSection} from './StepConfigFieldsTemplate';

export interface ShapeEditorV2Props {
  config: JSONObject;
  configShape: PipelineConfigJsonSpec;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  uiSchema: ShapeUISchemaV2;
}

/** @package */
export default function ShapeEditorV2({
  config,
  configShape,
  onChangeConfig,
  disabled,
  uiSchema,
}: ShapeEditorV2Props): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();

  function handleChangeConfig(config: JSONObject, path: ConfigShapePath, newValue: JSONValue): void {
    if (uiSchema?.[ON_CHANGE_CONFIG_MIDDLEWARE_KEY]) {
      onChangeConfig(
        uiSchema[ON_CHANGE_CONFIG_MIDDLEWARE_KEY](newValue, path, config, dataFramesInfos),
        path,
        newValue
      );
    } else {
      onChangeConfig(config, path, newValue);
    }
  }

  function shouldDisplayBanner(banner: FieldUISchemaBanner): boolean {
    if (banner.visibilityFn == null) {
      return true; // If no visibility function, always show
    }
    return banner.visibilityFn(config);
  }

  const fieldOrder = uiSchema[FIELD_ORDER_UI_SCHEMA_KEY];
  const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];
  const sectionInfoText = uiSchema?.[INFO_TEXT_UI_SCHEMA_KEY];
  const isCollapsible = uiSchema?.[IS_COLLAPSIBLE_SCHEMA_KEY];
  const numColumns = uiSchema?.[NUM_COLUMNS_KEY];

  if (fieldOrder.length > 0 && isShapeUISchemaV2NestedShapeValues(uiSchema)) {
    assert(numColumns == null, 'Num columns not supported with nested shapes');
    const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];
    const sectionInfoText = uiSchema?.[INFO_TEXT_UI_SCHEMA_KEY];
    const sectionNameOrNull = getSectionNameOrNull({sectionName, sectionInfoText});

    const fieldElements = fieldOrder.map((variableName) => (
      <StepConfigEditorRouterV2
        key={variableName}
        config={config}
        configShape={configShape}
        dataTestAttrs={{key: variableName}}
        onChangeConfig={handleChangeConfig}
        disabled={disabled}
        uiSchema={uiSchema[variableName]}
      />
    ));

    if (isCollapsible) {
      return <CollapsingSection sectionHeader={sectionNameOrNull}>{fieldElements}</CollapsingSection>;
    }

    return (
      <>
        {uiSchema[BANNER_KEY] && shouldDisplayBanner(uiSchema[BANNER_KEY]) && (
          <StepConfigEditorBanner {...uiSchema[BANNER_KEY]} />
        )}
        {sectionNameOrNull}
        {fieldElements}
      </>
    );
  }
  assert(
    isShapeUISchemaV2FieldComponentValue(uiSchema),
    'uiSchema must be a ShapeUISchemaV2FieldComponentValue'
  );
  const expandableDetails = uiSchema?.[EXPANDABLE_DETAILS_KEY];

  const fields = fieldOrder.map((variableName) => {
    const field = uiSchema[variableName];
    const isExpandable = variableName === expandableDetails?.headerField;
    assert(field != null, `Field ${variableName} is not defined in uiSchema`);

    const currentConfigShape = isFieldUISchemaWithStandardComponentV2(field)
      ? getConfigShapeAtConfigShapePath(field.configShapePath, configShape, config)
      : undefined;
    return {
      systemVariableName: variableName,
      // eslint-disable-next-line benchlint/no-in-operator
      variableName: 'displayName' in field ? field.displayName : undefined,
      // eslint-disable-next-line benchlint/no-in-operator
      infoText: 'infoText' in field ? field.infoText : undefined,
      required: currentConfigShape?.required,
      editor: (
        <StepConfigEditorRouterV2
          key={variableName}
          config={config}
          configShape={configShape}
          dataTestAttrs={{key: variableName}}
          onChangeConfig={handleChangeConfig}
          disabled={disabled}
          uiSchema={uiSchema[variableName]}
          isExpandable={isExpandable}
        />
      ),
    };
  });

  return (
    <>
      {uiSchema[BANNER_KEY] && shouldDisplayBanner(uiSchema[BANNER_KEY]) && (
        <StepConfigEditorBanner {...uiSchema[BANNER_KEY]} />
      )}
      <StepConfigFieldsTemplate
        numColumns={numColumns ?? 1}
        fields={fields}
        sectionName={sectionName}
        isCollapsible={isCollapsible}
        sectionInfoText={sectionInfoText}
        isCompact={true}
        expandableDetails={expandableDetails}
      />
      {uiSchema[SHOW_DIVIDER_LINE_KEY] && <hr className={css(styles.dividerLine)} />}
    </>
  );
}

const styles = StyleSheet.create({
  dividerLine: {
    marginTop: 4,
    marginBottom: 10,
    borderTop: `1px solid ${GRAY_LIGHT}`,
    width: '100%',
  },
});
